import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";

/**
 * Calculate linear regression line from data points
 * @param {Array} data - Array of [x, y] points
 * @returns {Array} - Array of regression line points
 */
const calculateLinearRegression = (data) => {
  try {
    // Validate input data
    if (!data || !Array.isArray(data) || data.length < 2) {
      console.warn(
        "Regression calculation requires at least 2 valid data points"
      );
      return [];
    }

    // Filter out invalid points
    const validData = data.filter(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        !isNaN(point[0]) &&
        !isNaN(point[1])
    );

    if (validData.length < 2) {
      console.warn("Not enough valid data points for regression");
      return [];
    }

    const n = validData.length;
    const sumX = validData.reduce((sum, point) => sum + point[0], 0);
    const sumY = validData.reduce((sum, point) => sum + point[1], 0);
    const sumXY = validData.reduce(
      (sum, point) => sum + point[0] * point[1],
      0
    );
    const sumX2 = validData.reduce((sum, point) => sum + point[0] ** 2, 0);

    // Handle division by zero
    const denominator = n * sumX2 - sumX ** 2;
    if (denominator === 0) {
      console.warn("Cannot calculate regression line - division by zero");
      return [];
    }

    const slope = (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;

    // Generate regression line points
    const minX = Math.min(...validData.map((point) => point[0]));
    const maxX = Math.max(...validData.map((point) => point[0]));

    return [
      { x: minX, y: slope * minX + intercept },
      { x: maxX, y: slope * maxX + intercept },
    ];
  } catch (error) {
    console.error("Error calculating regression line:", error);
    return [];
  }
};

const RegressionChart = ({ chartUtils }) => {
  const [series, setSeries] = useState([]);
  const [error, setError] = useState(null);
  useEffect(() => {
    try {
      if (!chartUtils?.series || !Array.isArray(chartUtils.series)) {
        setSeries([{ name: "No Data", type: "line", data: [] }]);
        return;
      }

      // Calculate regression line
      const regressionLine = calculateLinearRegression(chartUtils.series);

      // Prepare scatter data, with error handling for invalid points
      const scatterData = chartUtils.series
        .filter((point) => Array.isArray(point) && point.length === 2)
        .map(([x, y]) => ({
          x: isNaN(x) ? 0 : x,
          y: isNaN(y) ? 0 : y,
        }));

      if (scatterData.length === 0) {
        setSeries([{ name: "No Data", type: "line", data: [] }]);
        return;
      }

      // Set chart series data
      const newSeries = [
        {
          name: chartUtils?.colors?.[0]?.name || "Data Points",
          type: "scatter",
          data: scatterData,
        },
      ];

      // Only add regression line if calculation was successful
      if (regressionLine.length > 0) {
        newSeries.push({
          name: chartUtils?.colors?.[1]?.name || "Regression Line",
          type: "line",
          data: regressionLine,
        });
      }

      setSeries(newSeries);
      setError(null);
    } catch (err) {
      console.error("Error rendering regression chart:", err);
      setError("Failed to render chart. Please check your data.");
      setSeries([{ name: "Error", type: "line", data: [] }]);
    }
  }, [chartUtils]);
  // Chart options with error handling
  const options = {
    chart: {
      type: "line",
      zoom: {
        enabled: true,
        type: "xy",
      },
      animations: {
        enabled: true,
      },
      events: {
        error: function (chartContext, err) {
          console.error("Chart error:", err);
        },
      },
    },
    colors: [chartUtils?.colors?.[0]?.color, chartUtils?.colors?.[1]?.color],

    xaxis: {
      title: { text: chartUtils?.axis?.x || "X Axis" },
      min: undefined, // Let the chart determine appropriate boundaries
      tickAmount: 6,
      convertedCatToNumeric: true,
      labels: {
        formatter: function (val) {
          return val !== undefined ? val.toFixed(2) : "";
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#ccc",
      strokeDashArray: 3,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -10,
      background: {
        enabled: true,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#fff",
        padding: 5,
      },
      formatter: function (val) {
        return val !== undefined && !isNaN(val) ? val.toFixed(2) : "";
      },
    },
    yaxis: {
      title: { text: chartUtils?.axis?.y || "Y Axis" },
      min: undefined, // Let the chart determine appropriate boundaries
      tickAmount: 6,
      labels: {
        formatter: function (val) {
          return val !== undefined ? val.toFixed(2) : "";
        },
      },
    },
    stroke: {
      width: [0, 3],
    },
    markers: {
      size: 5,
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      x: {
        formatter: (val) => (isNaN(val) ? "Invalid value" : val.toFixed(2)),
      },
      y: {
        formatter: (val) => (isNaN(val) ? "Invalid value" : val.toFixed(2)),
      },
    },
    noData: {
      text: "No data available",
      align: "center",
      verticalAlign: "middle",
    },
  };

  // Render error message if there's an error
  if (error) {
    return (
      <div
        style={{
          height: "500px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ color: "red" }}>{error}</p>
      </div>
    );
  }
  return chartUtils?.chart_type === "REGRESSIONCHART" ? (
    <Chart options={options} series={series} type="line" height={350} />
  ) : (
    ""
  );
};

export default RegressionChart;
